import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { type TipperEmployeeProfile } from '../../../shared/api/tipper.service';
import { resolveCountryCodeByLanguage } from '../../../shared/config';
import {
  formatGoalProgressLabelByCountry,
  hasRatingReviews,
  normalizeNamePart,
  resolveRenderableAvatarUrl,
  resolveRatingValueLabel,
} from '../../../shared/model';

type TipCheckoutProfileContent = {
  avatarUrl: string | null;
  feedbackTitle: string;
  goalText: string;
  isRatingVisible: boolean;
  profileName: string;
  progressLabel: string | null;
  progressPercent: number | null;
  ratingValueLabel: string;
};

function calculateProgressPercent(currentAmount: number, targetAmount: number) {
  if (!Number.isFinite(currentAmount) || !Number.isFinite(targetAmount)) {
    return null;
  }

  if (targetAmount <= 0) {
    return null;
  }

  return Math.max(
    0,
    Math.min(100, Math.round((currentAmount / targetAmount) * 100)),
  );
}

function resolveProfileName(
  profile: TipperEmployeeProfile | null | undefined,
): string | null {
  const firstName = normalizeNamePart(profile?.firstName ?? '');
  const lastName = normalizeNamePart(profile?.lastName ?? '');

  if (firstName.length === 0 && lastName.length === 0) {
    return null;
  }

  return [firstName, lastName].filter(Boolean).join(' ');
}

function resolveFirstName(
  profile: TipperEmployeeProfile | null | undefined,
): string | null {
  const firstName = normalizeNamePart(profile?.firstName ?? '');

  return firstName.length > 0 ? firstName : null;
}

function resolveGoalText(
  profile: TipperEmployeeProfile | null | undefined,
): string | null {
  const goalText = profile?.goal.text.trim() ?? '';

  return goalText.length > 0 ? goalText : null;
}

export function useTipCheckoutProfileContent(
  recipientProfile?: TipperEmployeeProfile | null,
): TipCheckoutProfileContent {
  const { t, i18n } = useTranslation();
  const countryCode = resolveCountryCodeByLanguage(i18n.language);

  return useMemo(() => {
    const feedbackName = resolveFirstName(recipientProfile);
    const goalText =
      resolveGoalText(recipientProfile) ?? t('myPage.previewStory');
    const profileName =
      resolveProfileName(recipientProfile) ??
      t('tipsCheckout.profileNameFallback');
    const targetAmount = recipientProfile?.goal.targetAmount ?? null;
    const progressPercent =
      targetAmount === null
        ? null
        : calculateProgressPercent(
            recipientProfile?.goal.currentAmount ?? 0,
            targetAmount,
          );
    const isRatingVisible = hasRatingReviews(recipientProfile?.rating);

    return {
      avatarUrl: resolveRenderableAvatarUrl(recipientProfile?.avatarUrl),
      feedbackTitle:
        feedbackName !== null
          ? t('tipsCheckout.feedbackTitle', {
              name: feedbackName,
            })
          : t('tipsCheckout.feedbackTitleFallback'),
      goalText,
      isRatingVisible,
      profileName,
      progressLabel:
        progressPercent === null || targetAmount === null
          ? null
          : formatGoalProgressLabelByCountry(
              recipientProfile?.goal.currentAmount ?? 0,
              targetAmount,
              progressPercent,
              countryCode,
            ),
      progressPercent,
      ratingValueLabel: resolveRatingValueLabel(recipientProfile?.rating),
    };
  }, [countryCode, recipientProfile, t]);
}
